import React, { useCallback, useEffect, useState, useRef, createElement, Component } from 'react';
import { AnimatePresence } from 'framer-motion';
import { SlideNavigation } from './components/Shared';
import { Slide1, Slide2, Slide3, Slide4, Slide5 } from './components/slides/SlideGroup1';
import { Slide6, Slide7, Slide8, Slide9, Slide10 } from './components/slides/SlideGroup2';
import { Slide11, SlideNexa42View, Slide12, Slide13, Slide15 } from './components/slides/SlideGroup3';
// Declare custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'vexu-chat-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        'widget-key'?: string;
      }, HTMLElement>;
    }
  }
}
const slides = [Slide1, Slide2, Slide3, Slide4, Slide5, Slide6, Slide7, Slide8, Slide9, Slide10, Slide11, SlideNexa42View, Slide12, Slide13, Slide15];
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchRef = useRef<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } | null>(null);
  const nextSlide = useCallback(() => {
    setCurrentSlide(c => Math.min(c + 1, slides.length - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(c => Math.max(c - 1, 0));
  }, []);
  // Native touch event listeners for reliable swipe detection
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const minSwipeDistance = 50;
    const handleTouchStart = (e: TouchEvent) => {
      touchRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        endX: e.touches[0].clientX,
        endY: e.touches[0].clientY
      };
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!touchRef.current) return;
      touchRef.current.endX = e.touches[0].clientX;
      touchRef.current.endY = e.touches[0].clientY;
    };
    const handleTouchEnd = () => {
      if (!touchRef.current) return;
      const {
        startX,
        startY,
        endX,
        endY
      } = touchRef.current;
      const distanceX = startX - endX;
      const distanceY = startY - endY;
      // Only trigger if horizontal movement exceeds vertical
      if (Math.abs(distanceX) > Math.abs(distanceY) && Math.abs(distanceX) > minSwipeDistance) {
        if (distanceX > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      touchRef.current = null;
    };
    // Mouse drag handlers for desktop
    let mouseDown = false;
    let mouseStartX = 0;
    let mouseStartY = 0;
    const handleMouseDown = (e: MouseEvent) => {
      // Ignore clicks on buttons and interactive elements
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('a') || target.closest('input')) return;
      mouseDown = true;
      mouseStartX = e.clientX;
      mouseStartY = e.clientY;
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (!mouseDown) return;
      // Prevent text selection while dragging
      e.preventDefault();
    };
    const handleMouseUp = (e: MouseEvent) => {
      if (!mouseDown) return;
      mouseDown = false;
      const distanceX = mouseStartX - e.clientX;
      const distanceY = mouseStartY - e.clientY;
      if (Math.abs(distanceX) > Math.abs(distanceY) && Math.abs(distanceX) > minSwipeDistance) {
        if (distanceX > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
    };
    container.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: true
    });
    container.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });
    const handleMouseLeave = () => {
      mouseDown = false;
    };
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [nextSlide, prevSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  // Load Vexu AI Chat Widget script
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://api.vexu.ai/widget/vexu-widget.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://api.vexu.ai/widget/vexu-widget.js';
      script.type = 'module';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const CurrentSlideComponent = slides[currentSlide];
  const slideBgs = ['bg-white', 'bg-white', 'bg-white', 'bg-white', 'bg-mint-50', 'bg-mint-50', 'bg-white', 'bg-gray-50', 'bg-white', 'bg-gray-50', 'bg-white', 'bg-white', 'bg-mint-50', 'bg-white', 'bg-white' // 15: Closing
  ];
  return <div ref={containerRef} className={`relative w-screen h-[100svh]  overflow-hidden font-sans text-charcoal-900 selection:bg-vexu-500/20 flex flex-col transition-colors duration-700 ${slideBgs[currentSlide]}`} data-id="element-0">
      {/* Ambient Background Glows */}
      <div className={`absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[9.375rem] pointer-events-none transition-colors duration-700 ${currentSlide >= 1 && currentSlide <= 3 ? 'bg-red-500/5' : 'bg-vexu-500/5'}`} data-id="element-1" />
      <div className={`absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full blur-[9.375rem] pointer-events-none transition-colors duration-700 ${currentSlide >= 1 && currentSlide <= 3 ? 'bg-rose-400/5' : 'bg-vexu-400/5'}`} data-id="element-2" />

      {/* Subtle Grid Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgwLCAwLCAwLCAwLjAzKSIvPjwvc3ZnPg==')] pointer-events-none opacity-50" data-id="element-3" />

      {/* Slide Content Area */}
      <div className="flex-1 relative min-h-0" data-id="element-4">
        <AnimatePresence mode="wait" data-id="element-5">
          <CurrentSlideComponent key={currentSlide} data-id="element-6" />
        </AnimatePresence>
      </div>

      <SlideNavigation current={currentSlide} total={slides.length} onNext={nextSlide} onPrev={prevSlide} onGoTo={setCurrentSlide} data-id="element-7" />

      {/* Vexu AI Chat Widget
       <vexu-chat-widget
        widget-key="wgt_YyvMOLrHf0Evpt6yf8vIMEX3qxDoq4Gv"
        auto-open="false"
       ></vexu-chat-widget>
       */}
    </div>;
}